import { Subscription } from 'maplibre-gl';
import { BarbColorType, BarbSize, WeatherSettingsChangeSource, WindTemp } from '../../settings/settings';
import { MessageType } from '../../worker/actor/actor-messages';
import { SymbolGridLayer } from '../custom/symbol-grid/symbol-grid-layer';
import { TemperatureGridLayer } from '../custom/temperature/temperature-grid-layer';
import { WeatherLayerBase } from '../weather-layer-base';

export class WindBarbsLayer extends WeatherLayerBase {
    private barbsGridLayer: SymbolGridLayer | null = null;
    private temperatureGridLayer: TemperatureGridLayer | null = null;
    private themeSub: Subscription | null = null;

    protected onAdd(): void {
        this.themeSub = this.map.onThemeChanged(() => {
            if (this.barbsGridLayer) {
                this.barbsGridLayer.color = this.getColor();
            }
            if (this.temperatureGridLayer) {
                this.temperatureGridLayer.color = this.getColor();
            }
        });
        if (this.showWinds()) {
            this.barbsGridLayer = new SymbolGridLayer(this.baseId + '-symbols');
            this.barbsGridLayer.size = getBarbSize(this.settings.winds.barbSize);
            this.barbsGridLayer.color = this.getColor();
            this.barbsGridLayer.colorType = this.settings.winds.barbColorType;
            this.map.addLayer(this.barbsGridLayer, this.slotTopLayerId);
        }

        if (this.showTemperature()) {
            this.temperatureGridLayer = new TemperatureGridLayer(this.baseId + '-temperature');
            this.temperatureGridLayer.color = this.getColor();
            this.map.addLayer(this.temperatureGridLayer, this.slotTopLayerId);
        }
    }

    protected onRemove(): void {
        this.themeSub?.unsubscribe();
        if (this.barbsGridLayer) {
            this.map.removeLayer(this.barbsGridLayer.id);
            this.barbsGridLayer = null;
        }

        if (this.temperatureGridLayer) {
            this.map.removeLayer(this.temperatureGridLayer.id);
            this.temperatureGridLayer = null;
        }
    }

    protected onDisplayChanged(): void {
        if (this.barbsGridLayer) {
            this.map.setLayoutProperty(this.barbsGridLayer.id, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
        }

        if (this.temperatureGridLayer) {
            this.map.setLayoutProperty(this.temperatureGridLayer.id, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
        }
    }

    protected async loadFrame(frameId: number) {
        const response = await this.dispatcher.dispatch({
            type: MessageType.getWindBarbsData,
            data: {
                workspaceId: this.workspaceId,
                id: this.productId,
                frame: frameId
            }
        });

        if (!response) {
            return;
        }

        this.barbsGridLayer?.setData(response, getWindImageName);
        this.temperatureGridLayer?.setData(response);
    }

    protected async onSettingsUpdated(changeSource: WeatherSettingsChangeSource) {
        if (!windSettingsChanged(changeSource)) {
            return;
        }

        if (this.barbsGridLayer && changeSource === WeatherSettingsChangeSource.windsBarbSize) {
            this.barbsGridLayer.size = getBarbSize(this.settings.winds.barbSize);
        }

        if (changeSource === WeatherSettingsChangeSource.windsBarbsColor) {
            if (this.barbsGridLayer) {
                this.barbsGridLayer.color = this.getColor();
                this.barbsGridLayer.colorType = this.settings.winds.barbColorType;
            }

            if (this.temperatureGridLayer) {
                this.temperatureGridLayer.color = this.getColor();
            }
        }

        if (changeSource === WeatherSettingsChangeSource.windsTemp) {
            if (!this.barbsGridLayer && this.showWinds()) {
                this.barbsGridLayer = new SymbolGridLayer(this.baseId + '-symbols');
                this.barbsGridLayer.size = getBarbSize(this.settings.winds.barbSize);
                this.map.addLayer(this.barbsGridLayer, this.slotTopLayerId);
            }

            if (this.barbsGridLayer) {
                this.map.setLayoutProperty(this.barbsGridLayer!.id, 'visibility', this.showWinds() ? 'visible' : 'none');
            }

            if (!this.temperatureGridLayer && this.showTemperature()) {
                this.temperatureGridLayer = new TemperatureGridLayer(this.baseId + '-temperature');
                this.map.addLayer(this.temperatureGridLayer, this.slotTopLayerId);
            }

            if (this.temperatureGridLayer) {
                this.map.setLayoutProperty(this.temperatureGridLayer!.id, 'visibility', this.showTemperature() ? 'visible' : 'none');
            }
        }

        await this.loadProduct();
        if (this.visibleFrame !== null) {
            await this.loadFrame(this.visibleFrame);
        }
    }

    private showWinds(): boolean {
        return this.settings.winds.windTemp !== WindTemp.temperatureOnly;
    }

    private showTemperature(): boolean {
        return this.settings.winds.windTemp !== WindTemp.windOnly;
    }

    private getColor() {
        return this.settings.winds.barbColorType === BarbColorType.auto
            ? (this.map.getGlobalState()['text-color'] ?? '#000')
            : this.settings.winds.barbColorType === BarbColorType.fixed
              ? this.settings.winds.barbColor
              : null;
    }
}

function getWindImageName(input: number | null): string | null {
    if (input === null || input === 0.0) {
        return null;
    }
    // TODO: improve this mapping lol
    if (input <= 0) return 'fe-weather:wind-0';
    if (input <= 7) return 'fe-weather:wind-7';
    if (input <= 12) return 'fe-weather:wind-12';
    if (input <= 17) return 'fe-weather:wind-17';
    if (input <= 22) return 'fe-weather:wind-22';
    if (input <= 27) return 'fe-weather:wind-27';
    if (input <= 32) return 'fe-weather:wind-32';
    if (input <= 37) return 'fe-weather:wind-37';
    if (input <= 42) return 'fe-weather:wind-42';
    if (input <= 47) return 'fe-weather:wind-47';
    if (input <= 52) return 'fe-weather:wind-52';
    if (input <= 57) return 'fe-weather:wind-57';
    if (input <= 62) return 'fe-weather:wind-62';
    if (input <= 67) return 'fe-weather:wind-67';
    if (input <= 72) return 'fe-weather:wind-72';
    if (input <= 77) return 'fe-weather:wind-77';
    if (input <= 82) return 'fe-weather:wind-82';
    if (input <= 87) return 'fe-weather:wind-87';
    if (input <= 92) return 'fe-weather:wind-92';
    if (input <= 97) return 'fe-weather:wind-97';
    if (input <= 102) return 'fe-weather:wind-102';
    if (input <= 107) return 'fe-weather:wind-107';
    if (input <= 112) return 'fe-weather:wind-112';
    if (input <= 117) return 'fe-weather:wind-117';
    if (input <= 122) return 'fe-weather:wind-122';
    if (input <= 127) return 'fe-weather:wind-127';
    if (input <= 132) return 'fe-weather:wind-132';
    if (input <= 137) return 'fe-weather:wind-137';
    if (input <= 142) return 'fe-weather:wind-142';
    if (input <= 147) return 'fe-weather:wind-147';
    if (input <= 152) return 'fe-weather:wind-152';
    if (input <= 202) return 'fe-weather:wind-202';
    return null;
}

function getBarbSize(size: BarbSize): number {
    switch (size) {
        case BarbSize.small:
            return 22;
        case BarbSize.medium:
            return 35;
        case BarbSize.large:
            return 48;
        default:
            return 35;
    }
}

function windSettingsChanged(changeSource: WeatherSettingsChangeSource): boolean {
    return (
        changeSource === WeatherSettingsChangeSource.windsDepiction ||
        changeSource === WeatherSettingsChangeSource.windsBarbSize ||
        changeSource === WeatherSettingsChangeSource.windsTemp ||
        changeSource === WeatherSettingsChangeSource.windsBarbsColor
    );
}
